import { useState } from "react";
import { useDraft, useStore } from "../hooks.ts";
import { useNav } from "../Erfassung.tsx";
import {
  DRIVEWAY_LABEL, DRIVEWAY_TYPES, ELEVATOR_SIZES, ELEVATOR_SIZE_LABEL, PARKING_LABEL, PARKING_OPTIONS,
  STAIRWELL_LABEL, STAIRWELL_TYPES, STEPS_LABEL, STEPS_TO_DOOR,
} from "@umzugsscan/shared";
import { Check, Choice, Field, Footer, OptionalSelect } from "../ui/elemente.tsx";

/** Seit 30.10.2026 kurz (Betreiber): ab dem 5. OG gibt es ohnehin immer einen Aufzug, die genaue Etage zählt dann kaum. */
const FLOORS = [
  { value: -1, label: "Keller / Untergeschoss" },
  { value: 0, label: "Erdgeschoss" },
  ...[1, 2, 3, 4].map((n) => ({ value: n, label: `${n}. Obergeschoss` })),
  { value: 5, label: "5. Obergeschoss oder höher" },
];
const HIGH = 5;
/** Aufzug nur fragen, wo er offen ist: im Erdgeschoss egal (nein), ab dem 5. OG immer da (ja). */
const elevatorFor = (floor: number | null): boolean | null => (floor === 0 ? false : floor !== null && floor >= HIGH ? true : null);

const WALK_PRESETS = [5, 10, 20, 40];

export function Adresse(props: { kind: "from" | "to" }) {
  const d = useDraft();
  const store = useStore();
  const { weiter, zurueck } = useNav();
  const [tried, setTried] = useState(false);
  const a = d.addresses[props.kind];
  const set = (patch: Partial<typeof a>) => store.updateAddress(props.kind, patch);
  // Neue Adresse steht noch nicht fest (30.10.2026): dann nur PLZ und Ort.
  const pending = props.kind === "to" && a.pending;
  const autoElevator = elevatorFor(a.floor);
  const setFloor = (floor: number | null) => {
    const auto = elevatorFor(floor);
    // Automatisch gesetzten Aufzug zurücknehmen, wenn die Etage ihn wieder offen lässt.
    set({ floor, ...(auto !== null ? { elevator: auto } : elevatorFor(a.floor) !== null ? { elevator: null } : {}) });
  };

  const errors = {
    street: !pending && !a.street ? "Bitte Straße und Hausnummer angeben." : null,
    zip: !a.zip || !/^\d{5}$/.test(a.zip) ? "Bitte eine 5-stellige PLZ angeben." : null,
    city: !a.city ? "Bitte den Ort angeben." : null,
    floor: !pending && a.floor === null ? "Bitte die Etage wählen." : null,
    elevator: !pending && autoElevator === null && a.elevator === null ? "Bitte wählen." : null,
    walk: !pending && a.walkDistanceM === null ? "Bitte eine ungefähre Entfernung angeben." : null,
  };
  // Optionale Angaben: aufgeklappt, sobald schon etwas eingetragen ist (z. B. beim Zurückkommen).
  const hasOptional = [a.stairwell, a.elevatorSize, a.stepsToDoor, a.parking, a.driveway, a.note].some((v) => v !== null);
  const [open, setOpen] = useState(hasOptional);
  const ok = Object.values(errors).every((e) => e === null);
  const err = (k: keyof typeof errors) => (tried ? errors[k] : null);

  return (
    <>
      <h1>{props.kind === "from" ? "Wo ziehen Sie aus?" : "Wohin ziehen Sie?"}</h1>
      {d.company.demo && <p className="tip">Demo: Musterdaten sind schon eingetragen. Einfach weiter, oder ändern Sie, was Sie möchten.</p>}
      {props.kind === "to" && (
        <Check checked={a.pending} onChange={(v) => set({ pending: v })} hint="Dann genügen PLZ und Ort. Den Rest klären Sie später mit der Umzugsfirma.">
          Die neue Adresse steht noch nicht fest
        </Check>
      )}
      {!pending && (
        <Field
          label="Straße und Hausnummer"
          autoComplete="street-address"
          value={a.street}
          onChange={(v) => set({ street: v.trim() ? v : null })}
          error={err("street")}
        />
      )}
      <div className="row">
        <Field
          label="PLZ"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          value={a.zip}
          onChange={(v) => set({ zip: v.replace(/\D/g, "").slice(0, 5) || null })}
          error={err("zip")}
        />
        <Field label="Ort" autoComplete="address-level2" value={a.city} onChange={(v) => set({ city: v.trim() ? v : null })} error={err("city")} />
      </div>

      {!pending && (
      <>
      <div className={`field${err("floor") ? " field-error" : ""}`}>
        <label htmlFor={`floor-${props.kind}`}>Etage der Wohnung</label>
        <select
          id={`floor-${props.kind}`}
          value={a.floor === null ? "" : Math.max(-1, Math.min(HIGH, a.floor))}
          onChange={(e) => setFloor(e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">Bitte wählen</option>
          {FLOORS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        {err("floor") && <p className="error">{err("floor")}</p>}
      </div>

      {autoElevator === null && (
        <Choice
          label="Gibt es einen Aufzug?"
          value={a.elevator}
          options={[
            { value: true, label: "Ja" },
            { value: false, label: "Nein" },
          ]}
          onChange={(v) => set({ elevator: v })}
          error={err("elevator")}
        />
      )}

      <div className={`field${err("walk") ? " field-error" : ""}`}>
        <label htmlFor={`walk-${props.kind}`}>Laufweg zum nächsten Parkplatz (Meter)</label>
        <p className="hint">
          Von der Haustür bis zu der Stelle, an der ein Umzugswagen halten kann. Eine Schätzung genügt.
        </p>
        <div className="presets">
          {WALK_PRESETS.map((m) => (
            <button key={m} type="button" className={a.walkDistanceM === m ? "on" : ""} onClick={() => set({ walkDistanceM: m })}>
              {m} m
            </button>
          ))}
        </div>
        <input
          id={`walk-${props.kind}`}
          type="number"
          inputMode="numeric"
          min={0}
          max={1000}
          placeholder="andere Entfernung"
          value={a.walkDistanceM ?? ""}
          onChange={(e) => {
            const n = e.target.value === "" ? null : Math.min(1000, Math.max(0, Math.round(Number(e.target.value))));
            set({ walkDistanceM: n });
          }}
        />
        {err("walk") && <p className="error">{err("walk")}</p>}
      </div>
      </>
      )}

      {props.kind === "from" && (
        <div className="row">
          <div className="field">
            <label htmlFor="flaeche">Wohnfläche (m²)</label>
            <input
              id="flaeche"
              type="number"
              inputMode="numeric"
              min={10}
              max={2000}
              placeholder="optional"
              value={d.survey.livingAreaM2 ?? ""}
              onChange={(e) => store.updateSurvey({ livingAreaM2: e.target.value === "" ? null : Math.min(2000, Math.round(Number(e.target.value))) })}
            />
          </div>
          <div className="field">
            <label htmlFor="personen">Personen im Haushalt</label>
            <input
              id="personen"
              type="number"
              inputMode="numeric"
              min={1}
              max={30}
              placeholder="optional"
              value={d.survey.persons ?? ""}
              onChange={(e) => store.updateSurvey({ persons: e.target.value === "" ? null : Math.min(30, Math.round(Number(e.target.value))) })}
            />
          </div>
        </div>
      )}
      {props.kind === "from" && <p className="hint">Optional. Damit prüft der Umzugsbetrieb, ob die Menge zur Wohnung passt.</p>}

      {!pending && (
      <details className="optional" open={open} onToggle={(e) => setOpen(e.currentTarget.open)}>
        <summary>Optionale Angaben für ein genaueres Angebot</summary>
        <p className="hint">Kein Muss. Je mehr das Umzugsunternehmen vorab weiß, desto weniger muss es nachfragen.</p>
        <OptionalSelect label="Treppenhaus" value={a.stairwell} options={STAIRWELL_TYPES} labels={STAIRWELL_LABEL} onChange={(v) => set({ stairwell: v })} />
        {a.elevator === true && (
          <OptionalSelect label="Wie groß ist der Aufzug?" value={a.elevatorSize} options={ELEVATOR_SIZES} labels={ELEVATOR_SIZE_LABEL} onChange={(v) => set({ elevatorSize: v })} />
        )}
        <OptionalSelect label="Stufen vor dem Haus bis zur Haustür" value={a.stepsToDoor} options={STEPS_TO_DOOR} labels={STEPS_LABEL} onChange={(v) => set({ stepsToDoor: v })} />
        <OptionalSelect
          label="Parken und Halteverbot"
          value={a.parking}
          options={PARKING_OPTIONS}
          labels={PARKING_LABEL}
          onChange={(v) => set({ parking: v })}
          hint="Ein Halteverbot beantragt man bei der Stadt, meist einige Wochen vorher. Das Umzugsunternehmen berät Sie gern."
        />
        <OptionalSelect label="Zufahrt für den Umzugswagen" value={a.driveway} options={DRIVEWAY_TYPES} labels={DRIVEWAY_LABEL} onChange={(v) => set({ driveway: v })} />
        <Field
          label="Sonst noch etwas zum Zugang?"
          maxLength={500}
          placeholder="z. B. schmale Haustür, Gartentor, Baustelle vor dem Haus"
          value={a.note}
          onChange={(v) => set({ note: v.trim() ? v : null })}
        />
      </details>
      )}

      <Footer
        onBack={zurueck}
        onNext={() => {
          setTried(true);
          if (!ok) return;
          // Ältere Stände: Aufzug, der sich aus der Etage ergibt, nachtragen (die Frage ist dann ausgeblendet).
          if (!pending && autoElevator !== null && a.elevator !== autoElevator) set({ elevator: autoElevator });
          weiter();
        }}
      />
    </>
  );
}
